import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  useTheme,
  TablePagination,
} from '@mui/material';

import {
    Delete,
    Edit,
    FileUpload,
  Category,
  Person,
  Search,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Material, Meta } from '../../types';
import { materialApi } from '../../services/api';
import MaterialFormModal from '../../components/AddNewMaterialPopUp';
import { extractApiError, unwrapListResponse, unwrapMetaResponse, unwrapSingleResponse } from '../../apiUnwrapper/apiUnwrapper';
import { useSnackbar } from 'notistack';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  PageHeader,
} from '../../components/SharedPageParts';
import { useAuth } from '../../services/AuthContext';

const MaterialsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageMaterials = user?.roleName === 'Admin';
  const [open, setOpen] = useState(false);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [fileName, setFileName] = useState('');
  const [materialsMeta, setMaterialsMeta] = useState<Meta>({ total: 0, size: 5, page: 1 });
  const [errors, setErrors] = useState<Partial<Record<keyof Material, string>>>({});
  const [material, setMaterial] = useState<Material | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});
  const [confirmMessage, setConfirmMessage] = useState("Are you sure?");

  const handleSubmit = async (payload: Material) => {
      try {
        const result = unwrapSingleResponse<Material>(await materialApi.createUpdateMaterial(payload));
        enqueueSnackbar(
              isEditMode
                ? `Material updated! - ${result?.level1}`
                : `Material created! - ${result?.level1}`,
              { variant: "success" }
            );
        setOpen(false);
        setMaterial(null);
        setIsEditMode(false);
        loadMaterials();
      }
      catch (err) {
        const { message, fieldErrors } = extractApiError(err);
        enqueueSnackbar(
          isEditMode
            ? `Failed to update material! - ${message}`
            : `Failed to create material! - ${message}`,
          { variant: "error" }
        );
      }
  };

  function handleDelete(id: any): void {
      try{
          setConfirmAction(() => async () => {
          await materialApi.deleteMaterial(id);
          enqueueSnackbar("Material deleted successfully", { variant: "success" });
          loadMaterials();
        });
        setConfirmMessage("Are you sure you want to delete this Material?");
        setOpenConfirm(true);
      }
      catch(error){
        enqueueSnackbar("Error deleting Material: "+error , { variant: "error" });
        setError('Error deleting Material: '+error );
      }
    }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setFileName(file.name);
        // You can also upload or parse the file here
      }
  };
  useEffect(() => {
      loadMaterials();
   }, []);
        
          const loadMaterials = async (page = 1, size = 5) => {
            try {
              setLoading(true);
              setError(null);
              const result = await materialApi.getMaterials(page, size);
              const materialsData = unwrapListResponse<Material>(result);
              setMaterials(materialsData);
              setMaterialsMeta(unwrapMetaResponse<Meta>(result) || { total: 0, size: 5, page: 1 });
            } catch (err) {
              setError('Failed to load materials. Make sure the backend is running on port 8081.');
              console.error('Error loading materials:', err);
            } finally {
              setLoading(false);
            }
          };

          const filteredMaterials = materials.filter(material =>
            material.level1.toLowerCase().includes(searchTerm.toLowerCase()) ||
            material.level2.toLowerCase().includes(searchTerm.toLowerCase()) ||
            material.level3.toLowerCase().includes(searchTerm.toLowerCase()) ||
            material.level4.toLowerCase().includes(searchTerm.toLowerCase())
          );
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      {/* Main Content for Load Locations Page can be added here */}
      <Box className="MuiBox-root-margin" sx={{ px: 1, mx: 'auto' }}>
      <PageHeader
                                                    title="Materials Management"
                                                    Icon={Category}
                                                    onBack={() => navigate('/haulmasterlive')}
                                                    backLabel="Back to Haul Master"
                                                    subtitle="Manage material catalog and properties"
                                                  />

        {error && (
        <Alert severity="error" sx={{ mb: 3, backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, border: '1px solid #ef4444' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search materials by Level 1, Level 2, Level 3, or Level 4..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
                sx: { backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }
              }}
              sx={{ 
                flexGrow: 1, 
                minWidth: 250,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: '#64748b' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea' },
                },
                '& .MuiInputBase-input': { color: theme.palette.text.primary }
              }}
            />
            {canManageMaterials && (
              <>
            <Button variant="contained" sx={{
           background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
        }} onClick={() => {setMaterial(null); setIsEditMode(false); setOpen(true)}}>Add New Material</Button>
            <MaterialFormModal open={open} onClose={() => {setMaterial(null);setIsEditMode(false); setOpen(false); }} onSubmit={handleSubmit} material={material} isEdit={isEditMode}/>

      <Button
        variant="contained"
        component="label"
        startIcon={<FileUpload />}
        sx={{
           background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
        }}
      >
        Bulk Upload
        <input
          type="file"
          accept=".csv"
          hidden
          onChange={handleFileChange}
        />
      </Button>
      {fileName && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Selected file: <strong>{fileName}</strong>
        </Typography>
      )}
              </>
            )}

          </Box>
        </CardContent>
      </Card>

      <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
            Materials ({filteredMaterials.length})
          </Typography>
          <TablePagination
                      component="div"
                      count={materialsMeta.total}
                      page={materialsMeta.page - 1}
                      onPageChange={(e, newPage) => loadMaterials(newPage + 1, materialsMeta.size)}
                      rowsPerPage={materialsMeta.size}
                      onRowsPerPageChange={(e) => loadMaterials(1, parseInt(e.target.value, 10))}
                      rowsPerPageOptions={[5, 10, 15, 20]}
                      labelRowsPerPage="Materials per page"
                    />
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: theme.palette.background.default, borderColor: theme.palette.divider }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#2d3e50' }}>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Level 1</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Level 2</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Level 3</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Level 4</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Status</TableCell>
                  {canManageMaterials && (
                    <TableCell align="center" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMaterials.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canManageMaterials ? 6 : 5} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Person sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
                          No Material found
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          {searchTerm ? 'Try adjusting your search terms' : 'Check back later or add new materials.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMaterials.map((material) => (
                    <TableRow key={material.id} hover sx={{ '&:hover': { backgroundColor: '#2d3e50' }, borderBottom: '1px solid #475569' }}>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>{material.level1}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>{material.level2}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>{material.level3}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>{material.level4}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Chip label={material.isActive? "Active" : "Inactive"} size="small" />
                      </TableCell>
                      {canManageMaterials && (
                      <TableCell align="center" sx={{ borderBottom: 'none' }}>
                        <IconButton size="small" sx={{ color: '#06beb6' }}
                          onClick={() => {
                          setMaterial(material); // populate with existing data
                          setIsEditMode(true);
                          setOpen(true);
                          }}>
                        <Edit />
                        </IconButton>
                        <IconButton
                          key={material.id}
                          size="small"
                          sx={{ color: '#ef4444' }}
                          onClick={() => handleDelete(material.id)} // ✅ pass unit.id
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
                      component="div"
                      count={materialsMeta.total}
                      page={materialsMeta.page - 1}
                      onPageChange={(e, newPage) => loadMaterials(newPage + 1, materialsMeta.size)}
                      rowsPerPage={materialsMeta.size}
                      onRowsPerPageChange={(e) => loadMaterials(1, parseInt(e.target.value, 10))}
                      rowsPerPageOptions={[5, 10, 15, 20]}
                      labelRowsPerPage="Materials per page"
                    />
          <ConfirmDialog
                    open={openConfirm}
                    onClose={() => setOpenConfirm(false)}
                    onConfirm={confirmAction}
                    title="Confirm Delete"
                    message={confirmMessage}
                  />
        </CardContent>
      </Card>
      </Box>
    </Box>
  );
};

export default MaterialsPage;